import axios, { AxiosRequestHeaders } from "axios";
import { snapshot } from "valtio";
import {
  accessTokenStore,
  seamlessAccessToken,
  seamlessRefreshToken,
} from "../store/accessToken";
import storage from "../utils/storage";
import { ApiResponse, RefreshTokenRes } from "../types/types";

const devText = "development";
const isDev =
  process.env.NODE_ENV === devText || process.env.REACT_APP_ENV === devText;
const ProdUrl = "https://svbackend-production.up.railway.app/";
const DevUrl = "https://svbackend-production-45f5.up.railway.app/";
//
export const BaseUrl = !isDev ? ProdUrl : DevUrl;

export const FiBiInterviewWSEndpointTest =
  "wss://svbackend-production-45f5.up.railway.app/ai/interview-support-openai-websocket-test";
// const FiBiInterviewWSEndpointB2B =
//   "wss://svbackend-production-45f5.up.railway.app/ai/fibi-interview-voice-business-websocket";
// const ProdFiBiInterviewWSEndpointB2B =
//   "wss://svbackend-production.up.railway.app/ai/fibi-interview-voice-business-websocket";
const FiBiInterviewWSEndpointB2B =
  "wss://svbackend-production-45f5.up.railway.app/";
const ProdFiBiInterviewWSEndpointB2B =
  "wss://svbackend-production.up.railway.app/";

export const BaseFiBiInterviewWSEndpointB2B = !isDev
  ? ProdFiBiInterviewWSEndpointB2B
  : FiBiInterviewWSEndpointB2B;

const request = axios.create({
  baseURL: BaseUrl,
});

request.interceptors.request.use(
  (config) => {
    const accessToken = snapshot(accessTokenStore).accessToken;

    const newConfig = { ...config };

    if (accessToken) {
      newConfig.headers = {
        Authorization: `Bearer ${accessToken}`,
        ...newConfig.headers,
      } as AxiosRequestHeaders;
    }

    return newConfig;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor
request.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    if (error.response?.status === 400) {
      return Promise.reject(error.response?.data);
    }

    if (error.response?.status === 403) {
      return Promise.reject(
        "Sorry! You're not authorized to perform this action"
      );
    }

    const originalRequest = error.config;

    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // logout
      // loginFailed();
      // storage.clear();
      storage.remove(seamlessAccessToken);
      storage.remove(seamlessRefreshToken);

      try {
        const refreshToken = snapshot(accessTokenStore).refreshToken;
        if (!refreshToken) return;
        const response = await request.post<ApiResponse<RefreshTokenRes>>(
          "/user/refresh-token",
          {
            grant_type: "refresh_token",
            refresh_token: refreshToken,
          }
        );
        const { access_token, refresh_token } = response.data.data;
        // Update the access token and refresh token in localStorage
        storage.set(seamlessAccessToken, access_token);
        storage.set(seamlessRefreshToken, refresh_token);
        accessTokenStore.accessToken = access_token;
        accessTokenStore.refreshToken = refresh_token;

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return request(originalRequest);
      } catch (error) {
        // Handle refresh token error or redirect to login
        console.log(error, "error");
      }
    }
    return Promise.reject(error);
  }
);

// fetch with auth token and refresh token too
export const fetchWithAuth = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  let accessToken = storage.get(seamlessAccessToken);

  // Ensure Authorization header is set
  options.headers = {
    ...options.headers,
    Authorization: `Bearer ${accessToken}`,
  };

  const response = await fetch(`${BaseUrl}${url}`, options);

  // Log to debug what's actually coming back
  console.log("Response status:", response.status);

  // Check both HTTP status code and response body for 401
  if (
    response.status === 401 ||
    (response.headers.get("content-type")?.includes("application/json") &&
      (await response.clone().json()).code === 401)
  ) {
    // Try refreshing the token
    const refreshToken = storage.get(seamlessRefreshToken);
    if (!refreshToken) {
      throw new Error("No refresh token available. User must log in again.");
    }

    try {
      const refreshResponse = await request.post<ApiResponse<RefreshTokenRes>>(
        "/user/refresh-token",
        {
          grant_type: "refresh_token",
          refresh_token: refreshToken,
        }
      );

      const { access_token, refresh_token } = refreshResponse.data.data;

      // Store new tokens
      storage.set(seamlessAccessToken, access_token);
      storage.set(seamlessRefreshToken, refresh_token);

      // Retry the original request with the new token
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${access_token}`,
      };

      return fetch(url, options);
    } catch (error) {
      console.error("Token refresh failed:", error);
      throw new Error("Authentication failed. Please log in again.");
    }
  }

  return response;
};

export default request;
